/**
 * *"You appear to be offline"* — and, just as deliberately, nothing else.
 *
 * The checkbox has two halves and the second one is the load-bearing one: **queued action
 * submission is explicitly not implemented.** A `pendingAction` fired when the
 * socket came back, which for a non-idempotent ~$0.13 turn is a duplicate-turn generator
 * pointed at the player's wallet. So this component tells the player what
 * it knows and touches nothing: no disabled controls, no aborted stream, no retry, no queue.
 *
 * **A turn already running is left strictly alone**, which is not just conservatism. The
 * server finishes and commits a turn whose client went away, and `turn/reconcile.ts` is what
 * finds out; a banner that cancelled anything would be making a decision the reconcile path
 * exists to make properly, from the server's own answer rather than from a browser flag.
 *
 * And that flag is weak. `navigator.onLine` is false only when the OS says there is no link at
 * all — a laptop on a café network with a captive portal is *online* by this measure and can
 * reach nothing. So the wording hedges (*"appear"*), the banner is advisory, and the honest
 * error for a request that actually failed is still `ApiError('network')`, rendered where the
 * action was.
 */
import { useEffect, useState } from 'react';
import { useT } from '@/i18n/context';

export function OfflineNotice() {
  const t = useT();
  const online = useOnline();

  if (online) return null;

  return (
    <div
      // `status`, not `alert`: it is information, not something to interrupt for — and on the
      // story screen the reader may be a screen-reader user mid-narration.
      role="status"
      className="border-border bg-panel text-fg-muted fixed top-0 left-1/2 z-40 -translate-x-1/2 border border-t-0 px-4 py-2 font-mono"
      style={{ fontSize: 'var(--text-meta)' }}
    >
      {t('ui.offline.notice')}
    </div>
  );
}

/**
 * Whether the browser thinks it has a network.
 *
 * Read once on mount rather than at module scope, because `navigator.onLine` at import time is
 * a value from before the app existed, and a test environment that has no navigator would take
 * the module with it.
 */
function useOnline(): boolean {
  const [online, setOnline] = useState(true);

  useEffect(() => {
    const sync = (): void => {
      setOnline(navigator.onLine);
    };

    sync();
    window.addEventListener('online', sync);
    window.addEventListener('offline', sync);
    return () => {
      window.removeEventListener('online', sync);
      window.removeEventListener('offline', sync);
    };
  }, []);

  return online;
}
